import {
  forwardRef,
  TextareaHTMLAttributes,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TextArea, CharacterCount, ErrorMessage } from './TextArea.style';
import { ErrorSolidIcon } from '@icons';
import { FormLabel } from '..';

interface FormTextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  errorText?: string;
  label?: string;
  minHeight?: number;
  showCharacterCount?: boolean;
  tooltip?: {
    title: string;
    content: string;
  };
}

const FormTextArea = forwardRef<HTMLTextAreaElement, FormTextAreaProps>(
  (
    {
      errorText,
      label,
      minHeight,
      showCharacterCount,
      tooltip,
      value,
      defaultValue,
      onChange,
      maxLength,
      ...props
    },
    ref
  ) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);

    const [uncontrolledLength, setUncontrolledLength] = useState(() =>
      defaultValue === undefined || defaultValue === null ? 0 : String(defaultValue).length
    );

    const setRefs = useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    useLayoutEffect(() => {
      if (!showCharacterCount || value !== undefined) return;
      const el = innerRef.current;
      if (!el) return;
      const len = el.value.length;
      setUncontrolledLength((prev) => (prev !== len ? len : prev));
    });

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (showCharacterCount && value === undefined) {
          setUncontrolledLength(e.target.value.length);
        }
        onChange?.(e);
      },
      [onChange, value, showCharacterCount]
    );

    const characterCountText = useMemo(() => {
      const length = value !== undefined ? String(value ?? '').length : uncontrolledLength;
      if (maxLength !== undefined) {
        return `${length}/${maxLength}`;
      }
      return String(length);
    }, [value, uncontrolledLength, maxLength]);

    return (
      <div>
        {label && (
          <FormLabel htmlFor={props.id} tooltip={tooltip}>
            {label}
          </FormLabel>
        )}
        <TextArea
          {...props}
          ref={setRefs}
          hasError={!!errorText}
          minHeight={minHeight}
          value={value}
          defaultValue={defaultValue}
          onChange={showCharacterCount ? handleChange : onChange}
          maxLength={maxLength}
        />
        {showCharacterCount && (
          <CharacterCount aria-live="polite">{characterCountText}</CharacterCount>
        )}
        {errorText && (
          <ErrorMessage>
            <ErrorSolidIcon /> <span>{errorText}</span>
          </ErrorMessage>
        )}
      </div>
    );
  }
);

FormTextArea.displayName = 'FormTextArea';

export default FormTextArea;
